import type {
  LinePricingInfoData,
  PriceSource,
  PriceType,
  PricingReference,
} from '@/types/pricing';

// Raw reference object as returned inside a sale-order's sale lines
// (`reference1` / `reference2`).
interface RawSaleLineReference {
  customerDefaultCode?: string;
  orderDate?: string;
  saleOrder?: {
    name?: string;
    id?: string;
    erpId?: number;
    erpPricingStatus?: string;
    orderDate?: string;
  };
  partner?: {
    name?: string;
    id?: string;
  };
}

// The subset of a raw sale line (from `GET /sale-orders/:id`) that the mapper
// reads. Both the search view and the sale-order details table hit the same
// endpoint, so the same shape applies to both.
export interface RawSaleLineForPricing {
  suggestedPrice?: number | string | null;
  enginePrice?: number | string | null;
  // Pricing method (contract §3) + provenance (contract §1).
  priceType?: string | null;
  priceSource?: string | null;
  referenceProduct1?: string | null;
  referenceProduct2?: string | null;
  reference1?: RawSaleLineReference | null;
  reference2?: RawSaleLineReference | null;
  priceListId?: string | null;
  priceList?: { name?: string; effectiveDate?: string } | null;
  // Formula breakdown (contract): base price + per-meter adder, plus the line's
  // display unit for formatting the adder.
  basePrice?: number | string | null;
  perMeterAdder?: number | string | null;
  quotedProductLengthUnits?: string | null;
}

const toNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') return null;
  const parsed = typeof value === 'number' ? value : parseFloat(String(value));
  return Number.isNaN(parsed) ? null : parsed;
};

const normalizePriceType = (raw: string | null | undefined): PriceType => {
  switch ((raw ?? '').toString().toLowerCase()) {
    case 'price_list':
    case 'pricelist':
    case 'price list':
      return 'price_list';
    case 'exact':
      return 'exact';
    case 'series':
      return 'series';
    case 'reference_formula':
      return 'reference_formula';
    case 'cost':
      return 'cost';
    case 'static':
      return 'static';
    case 'formula':
      return 'formula';
    default:
      return null;
  }
};

const normalizePriceSource = (raw: string | null | undefined): PriceSource => {
  switch ((raw ?? '').toString().toLowerCase()) {
    case 'manual':
      return 'manual';
    case 'engine':
      return 'engine';
    default:
      return null;
  }
};

// Translate a raw sale line into the props LinePricingInfo expects. Single
// source of truth shared by the search views and the sale-order details table.
export function mapSaleLineToPricingInfo(
  saleLine: RawSaleLineForPricing | null | undefined,
): LinePricingInfoData {
  if (!saleLine) {
    return { price: null, priceType: null, priceSource: null, references: [] };
  }

  // Effective price = suggestedPrice ?? enginePrice (contract §5).
  const price = toNumber(saleLine.suggestedPrice) ?? toNumber(saleLine.enginePrice);
  const priceSource = normalizePriceSource(saleLine.priceSource);

  let priceType = normalizePriceType(saleLine.priceType);
  // Structural fallback for legacy rows that don't carry an explicit priceType.
  if (!priceType) {
    if (saleLine.reference2 || saleLine.referenceProduct2) priceType = 'series';
    else if (saleLine.reference1 || saleLine.referenceProduct1) priceType = 'exact';
    else if (saleLine.priceListId) priceType = 'price_list';
  }

  const references: PricingReference[] = [];
  const addReference = (ref: RawSaleLineReference | null | undefined) => {
    if (!ref) return;
    references.push({
      partNumber: ref.customerDefaultCode || 'Unknown',
      saleOrderName: ref.saleOrder?.name,
      saleOrderId: ref.saleOrder?.id,
      customerName: ref.partner?.name,
      orderDate: ref.saleOrder?.orderDate ?? ref.orderDate,
      orderState: ref.saleOrder?.erpPricingStatus,
    });
  };
  addReference(saleLine.reference1);
  addReference(saleLine.reference2);

  const pricelist =
    priceType === 'price_list' && saleLine.priceList?.name
      ? { name: saleLine.priceList.name, effectiveDate: saleLine.priceList.effectiveDate }
      : undefined;

  return {
    price,
    priceType,
    priceSource,
    references,
    pricelist,
    basePrice: toNumber(saleLine.basePrice),
    perMeterAdder: toNumber(saleLine.perMeterAdder),
    lengthUnit: saleLine.quotedProductLengthUnits ?? null,
  };
}
